import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, onSessionExpired } from '@shared/auth';
import type { DietEntry, DietEntryDraft, DietSettings } from '@diet/types';
import {
  estimateNutrition,
  fetchEstimateQuota,
} from '@diet/data/diet-repository';
import {
  addEntryMutationOptions,
  editEntryMutationOptions,
  removeEntryMutationOptions,
  saveSettingsMutationOptions,
} from '@diet/data/mutations';
import {
  NO_SETTINGS,
  entriesOptions,
  settingsOptions,
} from '@diet/data/queries';
import { useDietStore } from '@diet/stores/diet.store';

/**
 * 飲食紀錄的資料流程。元件只呼叫這裡的方法，不直接碰 query client 或 repository。
 *
 * 紀錄與設定是 server state，交給 TanStack Query 持有：載入中／失敗／重試、
 * 重新聚焦時的重新驗證都由它負責，這支只做三件 Query 不知道的事——把兩個查詢
 * 收斂成一組畫面狀態、把寫入結果併回快取、把「最近一次操作失敗」翻成人話。
 *
 * **回傳布林而非讓呼叫端接 Promise 的 rejection。**面板要靠這個布林決定
 * 「該不該關起來」，而失敗時保留使用者填的內容是刻意的（見各面板的說明）。
 */
export function useDiet() {
  const queryClient = useQueryClient();

  /*
   * 紀錄與設定併發取，串接只會讓開啟時間變成兩趟往返。
   *
   * 兩者的失敗不等價：設定查詢自帶降級（見 `settingsOptions`），只有紀錄的失敗
   * 會換掉整個畫面，設定載入失敗不影響記錄。
   */
  const entriesQuery = useQuery(entriesOptions());
  const settingsQuery = useQuery(settingsOptions());

  /**
   * 最近一次**寫入**失敗的訊息。
   *
   * 與查詢的錯誤分開：載入失敗要換掉整個畫面（沒有資料可顯示），但新增失敗時
   * 既有資料仍然有效，只需要提示。這是純粹的畫面狀態（使用者按「關閉」就該消失），
   * 故用 useState 而不是去讀各個 mutation 的 error——後者會在下一次送出時被重設，
   * 提示也就跟著閃掉。
   */
  const [actionError, setActionError] = useState<string | null>(null);

  /* session 失效時清掉殘留的提示；快取本身由 QueryProvider 清空 */
  useEffect(
    () =>
      onSessionExpired(() => {
        setActionError(null);
        useDietStore.getState().reset();
      }),
    [],
  );

  const addMutation = useMutation(addEntryMutationOptions(queryClient));
  const editMutation = useMutation(editEntryMutationOptions(queryClient));
  const removeMutation = useMutation(removeEntryMutationOptions(queryClient));
  const settingsMutation = useMutation(
    saveSettingsMutationOptions(queryClient),
  );

  /**
   * 送出一次寫入，並把結果翻成「成功了嗎」。
   *
   * 統一在這裡 catch，是為了讓四個寫入路徑的失敗行為完全一致：快取不動
   * （`onSuccess` 沒跑到）、畫面留在原地、錯誤以提示呈現。**後端沒成功就不更新
   * 畫面**——讓使用者看到一筆實際上沒存進去的紀錄，比直接報錯更糟。
   */
  const run = useCallback(
    async <TInput, TResult>(
      mutate: (input: TInput) => Promise<TResult>,
      input: TInput,
    ): Promise<boolean> => {
      try {
        await mutate(input);
        setActionError(null);
        return true;
      } catch (cause: unknown) {
        setActionError(messageOf(cause));
        return false;
      }
    },
    [],
  );

  const addEntry = useCallback(
    (draft: DietEntryDraft) => run(addMutation.mutateAsync, draft),
    [run, addMutation.mutateAsync],
  );

  const editEntry = useCallback(
    (entry: DietEntry) => run(editMutation.mutateAsync, entry),
    [run, editMutation.mutateAsync],
  );

  const removeEntry = useCallback(
    (id: string) => run(removeMutation.mutateAsync, id),
    [run, removeMutation.mutateAsync],
  );

  const saveSettings = useCallback(
    (settings: DietSettings) => run(settingsMutation.mutateAsync, settings),
    [run, settingsMutation.mutateAsync],
  );

  /*
   * 取出 refetch 再放進 deps，不要整個 query 物件——後者每次 render 都是新的參考，
   * useCallback 等於白寫。`refetch` 本身由 TanStack Query 保證穩定。
   */
  const { refetch: refetchEntries } = entriesQuery;
  const { refetch: refetchSettings } = settingsQuery;

  const retry = useCallback(() => {
    setActionError(null);
    void refetchEntries();
    void refetchSettings();
  }, [refetchEntries, refetchSettings]);

  const dismissError = useCallback(() => setActionError(null), []);

  return {
    /*
     * 畫面只需要知道三種情況之一，故把兩個查詢收斂成一個狀態。設定的失敗不列入——
     * 它已經降級成「沒有設定」，不該讓整個畫面變成錯誤頁。
     */
    status: toStatus(entriesQuery.isPending, entriesQuery.isError),
    entries: entriesQuery.data ?? [],
    settings: settingsQuery.data ?? NO_SETTINGS,
    /* 載入失敗與操作失敗共用同一個欄位：畫面依 status 決定要換頁還是只做提示 */
    error: entriesQuery.isError ? messageOf(entriesQuery.error) : actionError,
    retry,
    dismissError,
    addEntry,
    editEntry,
    removeEntry,
    saveSettings,
    /*
     * 直接轉出 repository 的函式，不包 useMutation：估算不改動任何快取狀態，
     * 也不需要 optimistic update 或失效重取。錯誤由表單自己以 alert 呈現 ——
     * 那是一次「使用者主動觸發、當場回應」的操作，不該進到全域的 actionError。
     */
    estimateNutrition,
    /*
     * 同樣直接轉出。額度不放進 TanStack Query 的快取：它每次估算都會變，而唯一的
     * 讀取者（表單面板）在開啟時取一次、之後由估算的回應帶回最新值 —— 快取在這裡
     * 只會多一份需要同步的真相。
     */
    fetchEstimateQuota,
  };
}

export type DietStatus = 'loading' | 'ready' | 'error';

function toStatus(isPending: boolean, isError: boolean): DietStatus {
  if (isError) return 'error';
  return isPending ? 'loading' : 'ready';
}

/**
 * 把失敗轉成**給使用者看的**一句話。
 *
 * session 過期要講清楚；`apiFetch` 收到 401 後 `AuthGate` 會隨即換上登入頁，
 * 這段訊息通常只閃現一瞬。
 *
 * **只有 `ApiError` 的訊息可以直接顯示**——那是後端刻意寫給人看的內容
 * （驗證訊息、額度用完），或 `apiFetch` 產生的「請求失敗（HTTP 500）」。
 * 其餘例外的 `message` 是給開發者看的字串（例如 Service Worker 的
 * `FetchEvent.respondWith received an error…`），只記 console，不顯示在畫面上。
 */
function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) {
    return cause.isUnauthenticated ? '登入已過期，請重新登入。' : cause.message;
  }

  /* 連不上後端（離線、連線中斷、DNS、SW 內的網路失敗）都會落在這裡 */
  console.error('[diet] 請求失敗', cause);

  return navigator.onLine
    ? '連線失敗，請稍後再試。'
    : '目前沒有網路連線，請連上網路後重試。';
}
